import { useCurrentUserContext } from '@/contexts';
import { useOrganizations, type Organization } from '@/hooks/useOrganizations';
import { OrganizationType } from '@/types/gql.generated';

type Ret = {
  // The home org resolved against the caller's memberships (personal
  // included); null when unresolvable — no home org, or the caller isn't a
  // member (e.g. editing via a direct share).
  organization: Organization | null;
  isPersonal: boolean;
  // The home org's id when it's a shared org the caller belongs to — the
  // value to scope getLibrary reads with (null = personal context).
  sharedOrgId: string | null;
  // Display name for the home workspace: the org's name, or the caller's
  // name/email for their personal org.
  workspaceName: string | null;
};

// Resolves which of the caller's workspaces an experience lives in.
// Callers treat null results as the personal context.
export const useExperienceHomeOrg = (
  experience?: { organizationId?: string | null } | null
): Ret => {
  const { currentUser } = useCurrentUserContext();
  const { organizations } = useOrganizations();

  const organization =
    (experience?.organizationId &&
      organizations.find((org) => org.id === experience.organizationId)) ||
    null;

  const isPersonal = organization?.type === OrganizationType.Personal;

  return {
    organization,
    isPersonal,
    sharedOrgId: organization && !isPersonal ? organization.id : null,
    workspaceName: organization
      ? isPersonal
        ? currentUser?.name || currentUser?.email || null
        : organization.name
      : null,
  };
};
